import React, { useState, useEffect } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import "./StreamsEvents.css";
import { ReactComponent as LeftArrow } from "../images/LeftArrow.svg";
import { ReactComponent as RightArrow } from "../images/RightArrow.svg";
import { ReactComponent as CloseIcon } from "../images/CloseIcon.svg";
import iHubLogo from "../images/ILH-HORIZONTAL-FULL-COLOR.jpg";
import config from "../config/config";
import HomeIcon from "../images/HomeIcon.svg";

const StreamsEvents = () => {
  const { RunId } = useParams(); // Extract RunId from URL
  const location = useLocation(); // Get state passed from StreamsRuns
  const { StreamName } = location.state || { StreamName: "Unknown Stream" }; // Retrieve StreamName from state

  const [eventsData, setEventsData] = useState([]); // Stream events data
  const [selectedEvent, setSelectedEvent] = useState(null); // Selected event details for popup
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [currentPage, setCurrentPage] = useState(1); // Pagination
  const rowsPerPage = 8; // Rows per page

  useEffect(() => {
    const fetchEventsData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch event data for the provided RunId
        const eventsResponse = await axios.get(
          `${config.API_BASE_URL}/default/Events?RunId=${RunId}`
        );
        setEventsData(eventsResponse.data || []);
      } catch (err) {
        setError(err.message || "Failed to fetch events. Please try again.");
        console.error("Error fetching events data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEventsData();
  }, [RunId]);

  // Pagination Logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = eventsData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(eventsData.length / rowsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleEventDetailsClick = (event) => {
    setSelectedEvent(event); // Set selected event details
  };

  const handleClosePopup = () => {
    setSelectedEvent(null); // Close popup
  };

  // Helper Function: Format Date | Time
  const formatDateTime = (dateTime) => {
    if (!dateTime) return "N/A";
    const date = new Date(dateTime);
    const formattedDate = date.toLocaleDateString(); // Format as MM/DD/YYYY or based on locale
    const formattedTime = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }); // Format as HH:MM AM/PM
    return `${formattedDate} | ${formattedTime}`;
  };

  return (
    <div className="streamsEvents-layout">
      {/* Header section with logo and title */}
      <div className="streamsEvents-header">
        <div className="streamsEvents-logo">
          <img src={iHubLogo} alt="iHubLogo" />
        </div>
        <h1 className="streamsEvents-title">Stream Events</h1>
        <Link to="/">
          <div className="streamEvents-homeButton">
            <img src={HomeIcon} alt="Home Icon" />
            <p class="home-btn-text">Home</p>
          </div>
        </Link>
      </div>

      {/* Filter Section */}
      <div className="streamsEvents-infoContainer">
        <div className="streamsEvents-filters">
          <div className="streamsEvents-filterGroup">
            <label
              htmlFor="streamsEvents-name"
              className="streamsEvents-filterLabel"
            >
              Stream Name
            </label>
            <div className="streamEvent-dataField">
              <p className="streamEvent-dataInfo">{StreamName}</p>
            </div>
          </div>
        </div>

        {/* Table Section */}
        {loading ? (
          <div className="streamPage-loading">
            <div className="spinner"></div>
            Loading Data...
          </div>
        ) : (
          <div className="streamsEvents-tableContainer">
            <table className="streamsEvents-table">
              <thead>
                <tr>
                  <th>Date Time</th>
                  <th>Action</th>
                  <th>Event</th>
                  <th>Event Details</th>
                </tr>
              </thead>
              <tbody>
                {error ? (
                  <tr>
                    <td colSpan="4" className="no-data-message">
                      {error}
                    </td>
                  </tr>
                ) : currentRows.length > 0 ? (
                  currentRows.map((event, index) => (
                    <tr key={index}>
                      <td>{formatDateTime(event.EventDateTime)}</td>
                      <td>{event.ActionName || "N/A"}</td>
                      <td>{event.EventName || "N/A"}</td>
                      <td>
                        {event.Parameters && event.Parameters.length > 0 ? (
                          <button
                            className="streamsEvents-detailsButton"
                            onClick={() => handleEventDetailsClick(event)}
                          >
                            View Details
                          </button>
                        ) : (
                          <span className="no-data-message">
                            No Event Details
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="no-data-message">
                      No events available for this Run.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Section */}
        {!loading && totalPages > 1 && (
          <div className="streamsEvents-pagination">
            <button
              className="streamsEvents-paginationButtonArrow"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <LeftArrow className="streamsPage-arrowIcon" />
            </button>
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                className={`streamsEvents-paginationButton ${
                  currentPage === index + 1 ? "active" : ""
                }`}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </button>
            ))}
            <button
              className="streamsEvents-paginationButtonArrow"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <RightArrow className="streamsEvents-arrowIcon" />
            </button>
          </div>
        )}
      </div>

      {/* Popup Window */}
      {selectedEvent && (
        <div className="streamsEvents-popup">
          <div className="streamsEvents-popupContent">
            <button
              className="streamsEvents-popupCloseButton"
              onClick={handleClosePopup}
            >
              <CloseIcon className="streamsEvents-closeIcon" />
            </button>
            <div className="streamsEvents-popupContainer">
              <table className="streamsEvents-table-popup">
                <thead>
                  <tr>
                    <th>Param Name</th>
                    <th>Values</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedEvent.Parameters &&
                  selectedEvent.Parameters.length > 0 ? (
                    selectedEvent.Parameters.map(
                      ({ ParamName, ParamValue }, index) => (
                        <tr key={index}>
                          <td>{ParamName}</td>
                          <td>{ParamValue}</td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td colSpan="2" className="no-data-message">
                        No parameters available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreamsEvents;
